import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useParams, useLocation } from 'react-router-dom';
import { reverse } from 'named-urls';

import { rrulestr } from 'rrule';
import dayjs from 'dayjs';
import { Formik, FormikHelpers } from 'formik';

import Record from 'ui/Record/Record';
import { Button } from 'components/Button';
import { MainHeader } from 'components/MainHeader/MainHeader';
import { CenteredCircularProgress } from 'components/Progress';
import NavigationBlocker from 'components/NavigationBlocker';

import { Tooltip } from '@mui/material';

import routes from 'core/routes';
import { EntityModules } from 'core/constants';

import { useNavBarHeight } from 'utils/hooks/navbar';
import { useStore } from 'utils/hooks/store';
import { timezoneStore } from 'stores/Timezone';

import { ScheduleFormValues, SchedulePauseReason } from 'utils/api/modules/schedules/schedules.types';

import GeneralDetailsSection from './components/GeneralDetailsSection';
import TimingSection from './components/TimingSection';
import PauseBanner from './components/PauseBanner';
import { Container, FormContainer, FormFooter } from './styles';
import {
  addWallClock,
  atWallClock,
  formatDurationIso,
  generateRRule,
  parseRRuleToFrequency,
  ruleStart,
  wallClockDuration,
} from './utils/rruleUtils';
import { scheduleZones } from './utils/zones';
import { splitPickerValues, toPickerValues } from './utils/performers';
import { createValidationSchema } from './utils';

const ScheduleDetails: React.FunctionComponent = () => {
  const navBarHeight = useNavBarHeight();
  const { routing, scheduleDetails, notification, session } = useStore();
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const location = useLocation();
  const shouldAllowNavigationRef = useRef(false);
  const [shouldAllowNavigation, setShouldAllowNavigation] = useState(false);

  const isNewSchedule = location.pathname === routes.dashboard.schedules.new;
  // Form source follows the flag (the backend gates it on the flag alone); nav placement follows the
  // module list, so the breadcrumb only points at Audits when the sidebar actually shows it.
  const auditsAsEntity = !!session.user?.auditsAsEntityEnabled;
  const auditsInNav = !!session.user?.activeEntityModules.includes(EntityModules.Audits);
  const details = scheduleDetails.details;
  // One signal for both the picker's options and the payload's key. Edits follow the schedule, not
  // the flag: a flag-on org still holds pre-cutover legacy schedules, and those stay on `template`.
  const usesSubmissionForm = isNewSchedule ? auditsAsEntity : details?.submissionForm != null;
  const isPaused = !isNewSchedule && Boolean(details?.isPaused);
  const pauseReason = details?.pauseReason ?? null;
  const pausedAt = details?.pausedAt ?? null;
  const pausedBy = details?.pausedBy ?? null;
  const pauseContext = details?.pauseContext ?? null;
  const zonesEnabled = !!session.user?.timezoneModelEnabled;
  const zoneIsDefault = !isNewSchedule && !!details?.timezoneIsDefault;
  // Fixed when the form opens: a reader zone that settles mid-edit must not reinitialise the form.
  const [readerZone] = useState(() => timezoneStore.readerZone);

  useEffect(() => {
    if (scheduleId && !isNewSchedule) {
      scheduleDetails.loadDetails(scheduleId);
    }
    // Reset navigation flag when route changes
    shouldAllowNavigationRef.current = false;
    setShouldAllowNavigation(false);
  }, [scheduleId, isNewSchedule]);

  const initialValues: ScheduleFormValues = useMemo(() => {
    if (isNewSchedule || !scheduleDetails.details) {
      const timezone = zonesEnabled ? readerZone : null;
      const today = new Date();
      const startDate = new Date(today);
      startDate.setHours(9, 0, 0, 0);
      const dueDate = new Date(today);
      dueDate.setHours(17, 0, 0, 0);
      const zoneToday = timezone ? dayjs().tz(timezone).format('YYYY-MM-DD') : '';

      return {
        name: '',
        template: null,
        assignees: [],
        site: null,
        asset: null,
        contact: null,
        startDate: timezone ? atWallClock(zoneToday, '09:00', timezone) : startDate,
        dueDate: timezone ? atWallClock(zoneToday, '17:00', timezone) : dueDate,
        frequency: 'one-off',
        timezone,
      };
    }

    // frequency is rrule: startDate is equal to dtstart and dueDate is equal to until
    // example "DTSTART:20120201T093000Z\nRRULE:FREQ=WEEKLY;INTERVAL=5;UNTIL=20130130T230000Z;BYDAY=MO,FR"

    const recurrence = rrulestr(scheduleDetails.details.recurrence);
    const startDate = ruleStart(recurrence);
    const { timezone, timezoneIsDefault } = scheduleDetails.details;
    const { ruleZone } = scheduleZones(timezone, timezoneIsDefault, zonesEnabled);

    // Calculate dueDate from startDate + duration, or use until if it exists
    let dueDate: Date;
    if (recurrence.options.until) {
      dueDate = new Date(recurrence.options.until);
    } else if (ruleZone) {
      // Counted on the clock it is sent on, so a first cycle across a clock change comes back unchanged.
      dueDate = addWallClock(startDate, scheduleDetails.details.duration, ruleZone);
    } else {
      // Parse duration string (e.g., "PT8H" for 8 hours) and add to startDate
      const durationObj = dayjs.duration(scheduleDetails.details.duration);
      dueDate = dayjs(startDate).add(durationObj).toDate();
    }

    return {
      name: scheduleDetails.details.name,
      template: scheduleDetails.details.template,
      assignees: toPickerValues(scheduleDetails.details.assignedTo, scheduleDetails.details.assignedGroups),
      site: scheduleDetails.details.site,
      asset: scheduleDetails.details.asset,
      contact: scheduleDetails.details.contact,
      startDate,
      dueDate,
      frequency: parseRRuleToFrequency(scheduleDetails.details.recurrence),
      timezone,
    };
  }, [isNewSchedule, scheduleDetails.details, zonesEnabled, readerZone]);

  // Bind schema to the loaded dueDate so an unchanged past first occurrence on a recurring schedule still validates.
  const validationSchema = useMemo(
    () => createValidationSchema({ initialDueDate: isNewSchedule ? null : initialValues.dueDate }),
    [isNewSchedule, initialValues.dueDate]
  );

  const handleCancel = (): void => {
    routing.push(reverse(routes.dashboard.schedules.toString()));
  };

  // Tracks the form's dirty flag so handleSubmit can decide between a resume-only payload (no edits)
  // and a full update. Formik's onSubmit signature does not pass `dirty` directly.
  const isDirtyRef = useRef(false);

  const handleSubmit = async (
    values: ScheduleFormValues,
    { setSubmitting, resetForm }: FormikHelpers<ScheduleFormValues>
  ): Promise<void> => {
    setSubmitting(true);

    const { ruleZone, dayZone } = scheduleZones(values.timezone, zoneIsDefault, zonesEnabled);
    const rrule = generateRRule(values.frequency, values.startDate!, ruleZone, dayZone);
    const duration = ruleZone
      ? wallClockDuration(values.startDate!, values.dueDate!, ruleZone)
      : formatDurationIso(values.startDate!, values.dueDate!);

    const { assignedTo, assignedGroups } = splitPickerValues(values.assignees);

    // Only send the form when it actually changed: create_version carries an unsupplied source
    // forward, so a rename can't 422 on a since-deleted form or on a flag rollback.
    const formSourceChanged = isNewSchedule || values.template?.uuid !== details?.template?.uuid;
    const formSource = usesSubmissionForm
      ? formSourceChanged && { submissionForm: { uuid: values.template!.uuid } }
      : { template: values.template! };

    const scheduleData = {
      name: values.name,
      ...formSource,
      assignedTo: assignedTo.map((assignee) => ({ uuid: assignee.uuid })),
      ...(session.user?.groupsEnabled ? { assignedGroups: assignedGroups.map((group) => ({ uuid: group.uuid })) } : {}),
      recurrence: rrule,
      duration,
      site: values.site ? { uuid: values.site.uuid } : null,
      asset: values.asset ? { uuid: values.asset.uuid } : null,
      contact: values.contact ? { uuid: values.contact.uuid } : null,
    };

    try {
      if (isNewSchedule) {
        const created = await scheduleDetails.createSchedule({
          ...scheduleData,
          ...(ruleZone ? { timezone: ruleZone } : {}),
        });
        if (created) {
          resetForm({ values });
          shouldAllowNavigationRef.current = true;
          setShouldAllowNavigation(true);
          routing.push(reverse(routes.dashboard.schedules.toString()));
        }
      } else if (scheduleId) {
        // When the form is clean and we're just resuming, send a resume-only payload so the
        // backend doesn't create a redundant ScheduleVersion.
        const updatePayload =
          isPaused && !isDirtyRef.current ? { resume: true } : { ...scheduleData, resume: isPaused };
        const updated = await scheduleDetails.updateSchedule(scheduleId, updatePayload);
        if (updated) {
          resetForm({ values });

          // If the schedule is still paused after save (resume rejected by backend, e.g. assignee
          // became inactive between load and save), keep the user on the page — leave the
          // NavigationBlocker armed so subsequent edits still prompt on navigation.
          if (!scheduleDetails.details?.isPaused) {
            shouldAllowNavigationRef.current = true;
            setShouldAllowNavigation(true);
            routing.push(reverse(routes.dashboard.schedules.toString()));
          }
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isNewSchedule && !scheduleDetails.isLoaded) {
    return <CenteredCircularProgress />;
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnMount
    >
      {({ isSubmitting, isValid, handleSubmit: formikHandleSubmit, validateForm, setFieldTouched, dirty, values }) => {
        isDirtyRef.current = dirty;
        // For paused schedules, "Save and Resume" reflects the live state's ability to resume.
        // Drive enable state from the form values so user edits update it immediately.
        const formHasActiveAssignees = values.assignees.length > 0;
        const formTemplateUuid = values.template?.uuid;
        const originalTemplateUuid = details?.template?.uuid;
        // Trust the picker (it only offers active templates) when the user changes template.
        const formTemplateIsActive =
          formTemplateUuid !== undefined && formTemplateUuid !== originalTemplateUuid
            ? true
            : (details?.templateIsActive ?? true);
        const canResume = formHasActiveAssignees && formTemplateIsActive;

        const saveAndResumeBlockedTooltip = ((): string => {
          if (canResume) return '';
          const fixes: string[] = [];
          if (!formHasActiveAssignees) fixes.push('add an active assignee or group');
          if (!formTemplateIsActive) fixes.push('reactivate or change the form template');
          return `To save, ${fixes.join(' and ')}.`;
        })();

        const saveLabel = isNewSchedule ? 'Add Schedule' : isPaused ? 'Save and Resume' : 'Save';
        const saveDisabled = isSubmitting || !isValid || (isPaused && !canResume);
        const saveDisabledTooltip = isPaused && !canResume ? saveAndResumeBlockedTooltip : '';

        return (
          <>
            <NavigationBlocker
              when={dirty && !shouldAllowNavigation}
              shouldBlockNavigation={() => dirty && !shouldAllowNavigationRef.current}
              navigateMessage="You have unsaved changes. Do you want to discard them or stay on this page?"
            />
            <Record
              disableMaxContentHeight
              navBarHeight={navBarHeight}
              Header={
                <MainHeader
                  title={isNewSchedule ? 'New Schedule' : 'Edit Schedule'}
                  breadcrumbs={[
                    ...(auditsInNav ? [{ label: 'Audits', link: routes.dashboard.audits.forms.toString() }] : []),
                    { label: 'Schedules', link: routes.dashboard.schedules.toString() },
                    {
                      label: isNewSchedule ? 'New Schedule' : 'Edit Schedule',
                      link: isNewSchedule
                        ? routes.dashboard.schedules.new.toString()
                        : reverse(routes.dashboard.schedules.details, { scheduleId: scheduleId as string }),
                    },
                  ]}
                />
              }
              Content={() => (
                <Container>
                  {isPaused && (
                    <PauseBanner
                      pauseReason={pauseReason ?? SchedulePauseReason.MANUAL}
                      pausedAt={pausedAt}
                      pausedBy={pausedBy}
                      pauseContext={pauseContext}
                    />
                  )}
                  <FormContainer>
                    <GeneralDetailsSection usesSubmissionForm={usesSubmissionForm} />
                    <TimingSection isNewSchedule={isNewSchedule} zoneIsDefault={zoneIsDefault} />
                  </FormContainer>
                  <FormFooter>
                    <Button onClick={handleCancel} variant="outlined" size="large">
                      Cancel
                    </Button>
                    <Tooltip title={saveDisabledTooltip} placement="bottom">
                      <span>
                        <Button
                          onClick={async () => {
                            const errors = await validateForm();
                            if (Object.keys(errors).length > 0) {
                              Object.keys(errors).forEach((field) => setFieldTouched(field, true));
                              const firstError = Object.values(errors)[0];
                              notification.enqueueErrorSnackbar(String(firstError));
                              return;
                            }
                            formikHandleSubmit();
                          }}
                          variant="contained"
                          size="large"
                          disabled={saveDisabled}
                        >
                          {saveLabel}
                        </Button>
                      </span>
                    </Tooltip>
                  </FormFooter>
                </Container>
              )}
            />
          </>
        );
      }}
    </Formik>
  );
};

export default observer(ScheduleDetails);
